import React, { useState, useEffect, useRef, useCallback } from "react";
import "./styles/base.css";
import "./styles/step1.css";

/*
  PolicyForm (stable, accessible)
  - Only merges saved/formData into local state once on mount (avoids parent overwrites).
  - Keeps inputs controlled and provides stable ids for accessibility.
  - Autosave remains (you can swap to a debounced autosave if desired).
*/

/*
  Pay links (no amount input):
  - PayPal uses paypal.me with your username (no amount appended).
  - Tithe.ly uses the direct donate URL you provided.
*/
const PAYPAL_LINK = "https://www.paypal.me/nlcca1914";

const POLICY_TEXT = {
  assumptionRisk: {
    title: "Assumption of Risk & Liability Release",
    text:
      "By signing below, I acknowledge that participation in Camp Fusion activities involves inherent risks, including but not limited to physical injury, illness, or death. I understand that these risks may arise from the camper’s own actions, the actions of others, or from the conditions of the facilities, equipment, or environment. On behalf of myself and the camper, I voluntarily assume all risks associated with participation and agree to release, waive, and hold harmless Camp Fusion, its directors, officers, employees, volunteers, agents, and affiliated organizations from any and all claims, demands, causes of action, or liability for any loss, injury, damage, or expense (including attorneys’ fees) arising out of the camper’s participation, except to the extent caused by gross negligence or willful misconduct. I further agree to indemnify and defend Camp Fusion against third-party claims arising from the camper’s acts or omissions."
  },
  medicalAuth: {
    title: "Medical Authorization",
    text:
      "I authorize Camp Fusion staff and any medical personnel acting on their behalf to provide, obtain, or secure emergency medical care for the camper if necessary. For campers staying in the dorms: any prescription or over‑the‑counter medication that the camper needs while staying overnight must be turned in to and administered by the camp nurse or an authorized medical designee. If a medical emergency occurs, the closest hospital is Culbertson Memorial (Rushville, IL), and the camper may be transported there or to another appropriate facility. I agree to be responsible for all costs of medical care and transportation for the camper."
  },
  photo: {
    title: "Photo / Media Release",
    text:
      "I grant Camp Fusion permission to photograph, film, and record the camper during camp activities and to use such media for promotional, educational, or informational purposes, including but not limited to publications, websites, social media, and advertising, without compensation. If I do not consent to media use, I will indicate so on this form and Camp Fusion will make reasonable efforts to exclude the camper from media capture where feasible."
  },
  codeOfConduct: {
    title: "Code of Conduct / Behavior Policy",
    text:
      "Camp Fusion expects all participants to behave respectfully and safely toward staff, volunteers, and other campers. Prohibited conduct includes but is not limited to: violence, bullying, harassment, possession of weapons, use or possession of illegal substances, and any activity that endangers others or damages facilities. Staff will address violations through corrective action up to and including dismissal from camp. If a camper is dismissed for disciplinary or safety reasons, camp leadership will contact the parent/guardian to arrange immediate pickup. Camp Fusion reserves the right to remove any participant whose behavior jeopardizes the safety of others. In such cases, the parent/guardian is responsible for transportation and any related costs."
  },
  emergency: {
    title: "Emergency & Evacuation Policy",
    text:
      "Camp Fusion maintains emergency procedures for severe weather, fire, medical emergencies, and other incidents. In the event of severe weather, campers will be moved to our designated storm shelter for protection. If the camp facility becomes structurally unsafe or if evacuation is otherwise required (for example, due to fire), campers will be relocated to First Southern Baptist Church, Rushville, IL. Camp Fusion staff will notify parents/guardians as soon as reasonably possible in an emergency, and will follow emergency responder guidance for transportation, sheltering, and reunification."
  },
  transportation: {
    title: "Transportation Consent",
    text:
      "I authorize Camp Fusion to transport the camper as needed for camp activities or in the case of a medical emergency. I understand that authorized transport may include camp vehicles, contracted transportation providers, or emergency services. I acknowledge that drivers will comply with applicable seat belt and safety laws and that Camp Fusion screens drivers and vehicles according to its safety policies."
  },
  background: {
    title: "Background Checks & Safeguarding Notice",
    text:
      "Camp Fusion conducts background checks on staff and volunteers who have direct contact with campers, and provides training on child safety and mandatory reporting. The camp complies with Illinois child protection and mandatory reporting laws and is committed to providing a safe environment."
  }
};

// stable required keys defined outside the component so they don't re-create each render
const REQUIRED_KEYS = ["assumptionRisk", "medicalAuth", "codeOfConduct", "emergency", "transportation", "background"];

export default function PolicyForm({ onAgree, formData = {} }) {
  const STORAGE_KEY = "policyForm_v1";

  const [agreements, setAgreements] = useState({
    assumptionRisk: false,
    medicalAuth: false,
    codeOfConduct: false,
    emergency: false,
    transportation: false,
    background: false,
    photo: false // opt-in
  });

  const [signature, setSignature] = useState("");
  const [relationship, setRelationship] = useState("");
  const [dateTime, setDateTime] = useState("");
  const [showPayment, setShowPayment] = useState(false);

  // Payment & other state
  const [paymentMethod, setPaymentMethod] = useState(formData.paymentMethod || "");
  const [churchName, setChurchName] = useState(formData.churchName || "");

  // Sponsorship (applicant-oriented) fields requested
  const [sponsorshipReason, setSponsorshipReason] = useState(formData.sponsorshipReason || "");
  const [fundraisingEfforts, setFundraisingEfforts] = useState(formData.fundraisingEfforts || ""); // "yes" | "no" | ""
  const [fundraisingDetails, setFundraisingDetails] = useState(formData.fundraisingDetails || "");
  const [scholarshipType, setScholarshipType] = useState(formData.scholarshipType || ""); // "full" | "partial" | ""
  const [partialPayAmount, setPartialPayAmount] = useState(formData.partialPayAmount || "");

  // Pay-ahead (boolean only; no amount)
  const [payAhead, setPayAhead] = useState(!!formData.payAhead);

  // Honeypot (should remain empty)
  const [website, setWebsite] = useState(formData.website || "");

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // mountedRef ensures we only merge saved/formData once on mount (prevents overwrites while user types)
  const mountedRef = useRef(false);

  // memoize hasAllRequired so it's stable and can be used as a dependency
  const hasAllRequired = useCallback((obj) => REQUIRED_KEYS.every(k => obj && obj[k]), []);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    // Load saved draft once
    try {
      const savedRaw = localStorage.getItem(STORAGE_KEY);
      if (savedRaw) {
        const parsed = JSON.parse(savedRaw);
        if (parsed.agreements) setAgreements(prev => ({ ...prev, ...parsed.agreements }));
        if (parsed.signature) setSignature(parsed.signature);
        if (parsed.relationship) setRelationship(parsed.relationship || "");
        if (parsed.dateTime) setDateTime(parsed.dateTime);
        if (parsed.paymentMethod) setPaymentMethod(parsed.paymentMethod);
        if (parsed.churchName) setChurchName(parsed.churchName);
        if (typeof parsed.website === "string") setWebsite(parsed.website);
        if (parsed.scholarshipType) setScholarshipType(parsed.scholarshipType);
        if (parsed.partialPayAmount) setPartialPayAmount(parsed.partialPayAmount);

        if (parsed.showPayment) {
          const ok = hasAllRequired(parsed.agreements) && parsed.signature && parsed.dateTime;
          if (ok) setShowPayment(true);
          else {
            parsed.showPayment = false;
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed)); } catch { }
          }
        }

        // load payment-related saved fields (if any)
        if (parsed.sponsorshipReason) setSponsorshipReason(parsed.sponsorshipReason);
        if (parsed.fundraisingEfforts) setFundraisingEfforts(parsed.fundraisingEfforts);
        if (parsed.fundraisingDetails) setFundraisingDetails(parsed.fundraisingDetails);
        if (parsed.payAhead) setPayAhead(!!parsed.payAhead);
      }
    } catch (e) {
      console.warn("PolicyForm: failed to load saved draft:", e);
    }

    // Merge parent formData only once (do not re-merge on subsequent prop changes)
    try {
      if (formData && Object.keys(formData).length > 0) {
        if (formData.agreements) setAgreements(prev => ({ ...prev, ...formData.agreements }));
        if (formData.signature) setSignature(formData.signature);
        if (formData.relationship) setRelationship(formData.relationship || "");
        if (formData.dateTime) setDateTime(formData.dateTime);
        if (formData.paymentMethod) setPaymentMethod(formData.paymentMethod);
        if (formData.churchName) setChurchName(formData.churchName || "");
        if (formData.sponsorshipReason) setSponsorshipReason(formData.sponsorshipReason);
        if (formData.fundraisingEfforts) setFundraisingEfforts(formData.fundraisingEfforts);
        if (formData.fundraisingDetails) setFundraisingDetails(formData.fundraisingDetails || "");
        if (formData.scholarshipType) setScholarshipType(formData.scholarshipType);
        if (formData.partialPayAmount) setPartialPayAmount(formData.partialPayAmount || "");
        if (formData.payAhead) setPayAhead(!!formData.payAhead);
        if (typeof formData.website === "string") setWebsite(formData.website);
      }
    } catch (e) {
      console.warn("PolicyForm: failed to merge initial formData:", e);
    }
  }, [formData, hasAllRequired]); // include deps to satisfy react-hooks/exhaustive-deps

  // Autosave (kept simple; optionally debounce this)
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          agreements,
          signature,
          relationship,
          dateTime,
          showPayment,
          paymentMethod,
          churchName,
          sponsorshipReason,
          fundraisingEfforts,
          fundraisingDetails,
          scholarshipType,
          partialPayAmount,
          payAhead,
          website
        })
      );
    } catch (e) {
      // ignore storage errors
    }
  }, [
    agreements,
    signature,
    relationship,
    dateTime,
    showPayment,
    paymentMethod,
    churchName,
    sponsorshipReason,
    fundraisingEfforts,
    fundraisingDetails,
    scholarshipType,
    partialPayAmount,
    payAhead,
    website
  ]);

  // Controlled handlers (policy/signature)
  function handleAgreementChange(e) {
    const { name, checked } = e.target;
    setAgreements(prev => ({ ...prev, [name]: !!checked }));
    setErrors(prev => ({ ...prev, agree: undefined }));
  }

  function handleSignatureChange(e) {
    setSignature(e.target.value);
    setErrors(prev => ({ ...prev, signature: undefined }));
  }

  function handleRelationshipChange(e) {
    setRelationship(e.target.value);
    setErrors(prev => ({ ...prev, relationship: undefined }));
  }

  function handleDateTimeChange(e) {
    setDateTime(e.target.value);
    setErrors(prev => ({ ...prev, dateTime: undefined }));
  }

  function handleAgreeAndSign(e) {
    e.preventDefault();

    // Honeypot: bots fill this, humans won't
    if (String(website || "").trim()) {
      // Fail silently
      return;
    }

    const err = {};
    if (!hasAllRequired(agreements)) err.agree = "Please agree to all required policies before continuing.";
    if (!signature || !signature.trim()) err.signature = "Signature is required.";
    if (!relationship || !relationship.trim()) err.relationship = "Relationship to camper is required.";
    if (!dateTime) err.dateTime = "Date/time is required.";
    setErrors(err);
    if (Object.keys(err).length > 0) return;

    setShowPayment(true);
  }

  // Validation for the payment selection (extended for sponsorship and youthGroup)
  function validatePaymentSelection() {
    const err = {};
    if (!paymentMethod) err.paymentMethod = "Please select a payment method.";

    if (paymentMethod === "youthGroup" && (!churchName || !churchName.trim())) {
      err.churchName = "Please enter the church / youth group name.";
    }

    if (paymentMethod === "sponsorship") {
      if (!sponsorshipReason || !sponsorshipReason.trim()) err.sponsorshipReason = "Please explain why you require sponsorship.";
      if (!fundraisingEfforts || (fundraisingEfforts !== "yes" && fundraisingEfforts !== "no")) {
        err.fundraisingEfforts = "Please indicate if you've raised funds or spoken with a local church about sponsorship.";
      }
      if (fundraisingEfforts === "yes" && (!fundraisingDetails || !fundraisingDetails.trim())) {
        // details optional by default
      }
      if (!scholarshipType || (scholarshipType !== "full" && scholarshipType !== "partial")) {
        err.scholarshipType = "Please select full or partial scholarship.";
      }

      if (scholarshipType === "partial") {
        if (!partialPayAmount || !String(partialPayAmount).trim()) {
          err.partialPayAmount = "Please enter how much you can pay.";
        }
      }
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  }

  async function handlePaymentSelect(e) {
    e.preventDefault();

    // Honeypot: bots fill this, humans won't
    if (String(website || "").trim()) {
      // Fail silently
      return;
    }

    if (!validatePaymentSelection()) return;

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      // ignore
    }

    const payload = {
      agreements,
      signature: signature.trim(),
      relationship: relationship.trim(),
      dateTime,
      paymentMethod,
      website: website.trim() || undefined,
      ...(paymentMethod === "youthGroup" ? { churchName: churchName.trim() } : {}),
      ...(paymentMethod === "sponsorship"
        ? {
          sponsorshipReason: sponsorshipReason.trim(),
          scholarshipType,
          partialPayAmount: scholarshipType === "partial" ? String(partialPayAmount || "").trim() : "",
          fundraisingEfforts,
          fundraisingDetails: fundraisingDetails || ""
        }
        : {}),
      payAhead: !!payAhead
    };

    setSubmitting(true);
    try {
      await onAgree(payload);
    } finally {
      setSubmitting(false);
    }
  }

  const inputId = (key) => `policy-${key}`;

  return (
    <>
      {!showPayment ? (
        <form className="form-container policy-form" onSubmit={handleAgreeAndSign} autoComplete="on">
          {/* Honeypot: hidden field to catch bots (should stay empty) */}
          <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
            <label>
              Website
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
              />
            </label>
          </div>

          <header>
            <h1 className="page-title">Policy & Signature</h1>
            <p className="muted small">Please read each policy below, agree, sign, and provide the date/time to continue.</p>
          </header>

          <div className="field">
            {Object.keys(POLICY_TEXT).map((key) => {
              const p = POLICY_TEXT[key];
              const isOptional = key === "photo";
              return (
                <div key={key} style={{ marginBottom: "1.25rem" }}>
                  <h3 style={{ margin: "0 0 0.5rem 0" }}>{p.title}</h3>
                  <div style={{ marginBottom: "0.5rem", color: "#333", lineHeight: 1.5 }}>
                    {p.text}
                  </div>

                  <label className="checkbox-label" htmlFor={inputId(key)} style={{ display: "block", marginTop: "0.25rem" }}>
                    <input
                      id={inputId(key)}
                      name={key}
                      type="checkbox"
                      checked={!!agreements[key]}
                      onChange={handleAgreementChange}
                      style={{ marginRight: 8 }}
                    />
                    {isOptional ? `I consent to the ${p.title}.` : `I have read and agree to the ${p.title}.`}
                  </label>
                </div>
              );
            })}
            {errors.agree && <div className="field-error">{errors.agree}</div>}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="policy-signature">Signature (type full name) <span className="required">*</span></label>
            <input id="policy-signature" className="input" value={signature} onChange={handleSignatureChange} />
            {errors.signature && <div className="field-error">{errors.signature}</div>}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="policy-relationship">Relationship to camper (e.g. Parent/Guardian,Self) <span className="required">*</span></label>
            <input id="policy-relationship" className="input" value={relationship} onChange={handleRelationshipChange} />
            {errors.relationship && <div className="field-error">{errors.relationship}</div>}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="policy-datetime">Date / Time <span className="required">*</span></label>
            <input id="policy-datetime" type="datetime-local" className="input" value={dateTime} onChange={handleDateTimeChange} />
            {errors.dateTime && <div className="field-error">{errors.dateTime}</div>}
          </div>

          <div className="form-footer">
            <button type="submit" className="btn btn-primary">Agree & Continue</button>
          </div>
        </form>
      ) : (
        <form className="form-container policy-form" onSubmit={handlePaymentSelect} autoComplete="on">
          {/* Honeypot: hidden field to catch bots (should stay empty) */}
          <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
            <label>
              Website
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
              />
            </label>
          </div>

          <header>
            <h2>Payment Options</h2>
            <p className="muted small">Select how you would like to pay for camp. Payment is not required at registration.</p>
          </header>

          {/* Payment method selection (emphasize youthGroup & sponsorship) */}
          <div className="field policy-section">
            <label className="field-label" htmlFor="policy-payment">
              How would you like to pay for camp? <span className="required">*</span>
            </label>
            <select
              id="policy-payment"
              name="paymentMethod"
              className="select"
              value={paymentMethod}
              onChange={(e) => {
                setPaymentMethod(e.target.value);
                // reset sponsorship-specific answers if switching away
                if (e.target.value !== "sponsorship") {
                  setSponsorshipReason("");
                  setFundraisingEfforts("");
                  setFundraisingDetails("");
                  setScholarshipType("");
                  setPartialPayAmount("");
                }
                if (e.target.value !== "youthGroup") setChurchName("");
                setErrors(prev => ({ ...prev, paymentMethod: undefined, churchName: undefined, sponsorshipReason: undefined, fundraisingEfforts: undefined, fundraisingDetails: undefined, scholarshipType: undefined, partialPayAmount: undefined }));
              }}
              required
            >
              <option value="">Select</option>
              <option value="card">Card/PayPal</option>
              <option value="check">Check</option>
              <option value="cash">Cash</option>
              <option value="youthGroup">With a Youth Group</option>
              <option value="sponsorship">Sponsorship</option>
            </select>
            {errors.paymentMethod && <div className="field-error">{errors.paymentMethod}</div>}
          </div>

          {/* Youth group block */}
          {paymentMethod === "youthGroup" && (
            <div className="field">
              <label className="field-label" htmlFor="policy-church">Church / Youth group name <span className="required">*</span></label>
              <input
                id="policy-church"
                name="churchName"
                className="input"
                value={churchName}
                onChange={(e) => {
                  setChurchName(e.target.value);
                  setErrors(prev => ({ ...prev, churchName: undefined }));
                }}
                placeholder="Name of church or youth group"
              />
              {errors.churchName && <div className="field-error">{errors.churchName}</div>}
            </div>
          )}

          {/* Sponsorship applicant questions */}
          {paymentMethod === "sponsorship" && (
            <div className="field sponsorship-block">
              <label className="field-label">Why do you require sponsorship? <span className="required">*</span></label>
              <input
                name="sponsorshipReason"
                className="input"
                value={sponsorshipReason}
                onChange={(e) => {
                  setSponsorshipReason(e.target.value);
                  setErrors(prev => ({ ...prev, sponsorshipReason: undefined }));
                }}
                placeholder="e.g. student, single parent, unexpected hardship"
                required
              />
              {errors.sponsorshipReason && <div className="field-error">{errors.sponsorshipReason}</div>}

              <div style={{ marginTop: 12 }}>
                <label className="field-label">
                  Do you need a full or partial scholarship? <span className="required">*</span>
                </label>
                <div style={{ display: "flex", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="radio"
                      name="scholarshipType"
                      value="full"
                      checked={scholarshipType === "full"}
                      onChange={(e) => {
                        setScholarshipType(e.target.value);
                        setPartialPayAmount("");
                        setErrors((prev) => ({ ...prev, scholarshipType: undefined, partialPayAmount: undefined }));
                      }}
                    />
                    Full
                  </label>

                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="radio"
                      name="scholarshipType"
                      value="partial"
                      checked={scholarshipType === "partial"}
                      onChange={(e) => {
                        setScholarshipType(e.target.value);
                        setErrors((prev) => ({ ...prev, scholarshipType: undefined }));
                      }}
                    />
                    Partial
                  </label>
                </div>
                {errors.scholarshipType && <div className="field-error">{errors.scholarshipType}</div>}
              </div>

              {scholarshipType === "partial" && (
                <div style={{ marginTop: 12 }}>
                  <label className="field-label">
                    How much can you pay? <span className="required">*</span>
                  </label>
                  <input
                    name="partialPayAmount"
                    className="input"
                    value={partialPayAmount}
                    onChange={(e) => {
                      setPartialPayAmount(e.target.value);
                      setErrors((prev) => ({ ...prev, partialPayAmount: undefined }));
                    }}
                    placeholder="e.g. 50, 100, 200"
                  />
                  {errors.partialPayAmount && <div className="field-error">{errors.partialPayAmount}</div>}
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <label className="field-label">Have you done anything to help fundraise or spoken with a local church about sponsorship? <span className="required">*</span></label>
                <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="radio"
                      name="fundraisingEfforts"
                      value="yes"
                      checked={fundraisingEfforts === "yes"}
                      onChange={(e) => { setFundraisingEfforts(e.target.value); setErrors(prev => ({ ...prev, fundraisingEfforts: undefined })); }}
                    />
                    Yes
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="radio"
                      name="fundraisingEfforts"
                      value="no"
                      checked={fundraisingEfforts === "no"}
                      onChange={(e) => { setFundraisingEfforts(e.target.value); setErrors(prev => ({ ...prev, fundraisingEfforts: undefined })); }}
                    />
                    No
                  </label>
                </div>
                {errors.fundraisingEfforts && <div className="field-error">{errors.fundraisingEfforts}</div>}
              </div>

              {fundraisingEfforts === "yes" && (
                <div style={{ marginTop: 12 }}>
                  <label className="field-label">Please tell us what you've done (optional)</label>
                  <textarea
                    name="fundraisingDetails"
                    className="input"
                    value={fundraisingDetails}
                    onChange={(e) => setFundraisingDetails(e.target.value)}
                    placeholder="e.g., held a bake sale, asked local church 'St. John's'"
                    rows={4}
                  />
                  {errors.fundraisingDetails && <div className="field-error">{errors.fundraisingDetails}</div>}
                </div>
              )}

              <p className="muted" style={{ marginTop: 12 }}>
                Please note, we may reach out to you for more information regarding your sponsorship request.
              </p>
            </div>
          )}

          {/* Optional pay-ahead (links only) */}
          <div className="field" style={{ marginTop: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                name="payAhead"
                checked={payAhead}
                onChange={(e) => {
                  setPayAhead(e.target.checked);
                }}
              />
              I want to pay ahead (optional)
            </label>

            {payAhead && (
              <>
                <p className="muted" style={{ marginTop: 12 }}>
                  Pay ahead is processed externally. We currently accept pre-payments via PayPal.
                  After completing payment using the link below, return to finish the registration.
                </p>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    marginTop: 8,
                    flexWrap: "wrap",
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                >
                  <a
                    className="btn btn-primary"
                    href={PAYPAL_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open PayPal donation link in a new tab"
                  >
                    Pay with PayPal
                  </a>
                </div>
              </>
            )}
          </div>

          <div className="form-footer">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Complete Registration"}
            </button>
          </div>
        </form>
      )}
    </>
  );
}